import {Component, computed, signal, ChangeDetectionStrategy} from "@angular/core"
import {InputContainerComponent} from "@common/components/inputs/input-container/input-container.component"
import {NumericInputComponent} from "@common/components/inputs/numeric-input/numeric-input.component"
import {ToggleComponent} from "@common/components/buttons/toggle/toggle.component"
import {BaseInspectorComponent} from "@template-editor/components/inspectors/base-inspector/base-inspector.component"
import {InspectorSectionComponent} from "@template-editor/components/inspectors/inspector-section/inspector-section.component"
import {ValueSlotComponent, SelectionPossibilities, SelectionPossibility} from "@template-editor/components/value-slot/value-slot.component"
import {
    TransformMatrix,
    isTransformMatrixWithParametersTranslationRotationScale,
    isTransformMatrixWithParametersMatrixArray,
    isTransformMatrixWithParametersProduct,
    TransformMatrixParametersTranslationRotationScale,
    TransformMatrixParametersMatrixArray,
    TransformMatrixParametersProduct,
    transformMatrixFromTranslationRotationScale,
    from3dsMaxTransform,
    to3dsMaxTransform,
} from "@cm/template-nodes"
import {Vector3} from "@cm/math"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-transform-matrix-inspector",
    templateUrl: "./transform-matrix-inspector.component.html",
    styleUrl: "./transform-matrix-inspector.component.scss",
    imports: [ValueSlotComponent, InspectorSectionComponent, InputContainerComponent, NumericInputComponent, ToggleComponent],
})
export class TransformMatrixInspectorComponent extends BaseInspectorComponent<TransformMatrix> {
    readonly $useMaxCoordinates = signal(true)

    readonly $maxTransform = computed(() => {
        this.$parameters()
        const trsNode = this.$transformMatrixWithParametersTranslationRotationScale()
        if (!trsNode) return undefined
        const {translation, rotation, scale} = trsNode.parameters.transform
        if (
            typeof translation[0] !== "number" ||
            typeof translation[1] !== "number" ||
            typeof translation[2] !== "number" ||
            typeof rotation[0] !== "number" ||
            typeof rotation[1] !== "number" ||
            typeof rotation[2] !== "number" ||
            typeof scale[0] !== "number" ||
            typeof scale[1] !== "number" ||
            typeof scale[2] !== "number"
        )
            return undefined

        const nativeTranslation = new Vector3(translation[0], translation[1], translation[2])
        const nativeRotationRd = new Vector3((rotation[0] * Math.PI) / 180, (rotation[1] * Math.PI) / 180, (rotation[2] * Math.PI) / 180)
        const nativeScale = new Vector3(scale[0], scale[1], scale[2])
        const matrix = transformMatrixFromTranslationRotationScale(nativeTranslation, nativeRotationRd, nativeScale)
        return to3dsMaxTransform(matrix, false)
    })

    readonly $maxTranslateX = computed(() => this.$maxTransform()?.position.x ?? 0)
    readonly $maxTranslateY = computed(() => this.$maxTransform()?.position.y ?? 0)
    readonly $maxTranslateZ = computed(() => this.$maxTransform()?.position.z ?? 0)
    readonly $maxRotationX = computed(() => (this.$maxTransform()?.rotation.x ?? 0) * (180 / Math.PI))
    readonly $maxRotationY = computed(() => (this.$maxTransform()?.rotation.y ?? 0) * (180 / Math.PI))
    readonly $maxRotationZ = computed(() => (this.$maxTransform()?.rotation.z ?? 0) * (180 / Math.PI))

    private getCurrentMaxTransform() {
        const node = this.$node()
        if (!isTransformMatrixWithParametersTranslationRotationScale(node)) return undefined
        const {translation, rotation, scale} = node.parameters.transform
        if (
            typeof translation[0] !== "number" ||
            typeof translation[1] !== "number" ||
            typeof translation[2] !== "number" ||
            typeof rotation[0] !== "number" ||
            typeof rotation[1] !== "number" ||
            typeof rotation[2] !== "number" ||
            typeof scale[0] !== "number" ||
            typeof scale[1] !== "number" ||
            typeof scale[2] !== "number"
        )
            return undefined

        const nativeTranslation = new Vector3(translation[0], translation[1], translation[2])
        const nativeRotationRd = new Vector3((rotation[0] * Math.PI) / 180, (rotation[1] * Math.PI) / 180, (rotation[2] * Math.PI) / 180)
        const nativeScale = new Vector3(scale[0], scale[1], scale[2])
        const matrix = transformMatrixFromTranslationRotationScale(nativeTranslation, nativeRotationRd, nativeScale)
        return to3dsMaxTransform(matrix, false)
    }

    private setMaxValue(key: "x" | "y" | "z", value: number, mode: "translate" | "rotate") {
        const transform = this.getCurrentMaxTransform()
        if (!transform) return
        const position = transform.position.copy()
        const rotation = transform.rotation.copy()
        const scale = transform.scale.copy()

        if (mode === "translate") position[key] = value
        else rotation[key] = value * (Math.PI / 180)

        const nativeMatrix = from3dsMaxTransform(position, rotation, scale, false)
        const {position: nPos, quaternion, scale: nScale} = nativeMatrix.decompose()
        const nRot = quaternion.toEuler("XYZ")

        this.sceneManagerService.modifyTemplateGraph(() => {
            this.$node().updateParameters({
                transform: {
                    type: "TranslationRotationScale" as const,
                    translation: [nPos.x, nPos.y, nPos.z],
                    rotation: [(nRot.x * 180) / Math.PI, (nRot.y * 180) / Math.PI, (nRot.z * 180) / Math.PI],
                    scale: [nScale.x, nScale.y, nScale.z],
                },
            })
        })
    }

    setMaxTranslateX(value: number | null | undefined) {
        if (value != null) this.setMaxValue("x", value, "translate")
    }
    setMaxTranslateY(value: number | null | undefined) {
        if (value != null) this.setMaxValue("y", value, "translate")
    }
    setMaxTranslateZ(value: number | null | undefined) {
        if (value != null) this.setMaxValue("z", value, "translate")
    }
    setMaxRotationX(value: number | null | undefined) {
        if (value != null) this.setMaxValue("x", value, "rotate")
    }
    setMaxRotationY(value: number | null | undefined) {
        if (value != null) this.setMaxValue("y", value, "rotate")
    }
    setMaxRotationZ(value: number | null | undefined) {
        if (value != null) this.setMaxValue("z", value, "rotate")
    }

    readonly $transformTypeSelectionPossibilities = signal<
        SelectionPossibilities<TransformMatrixParametersTranslationRotationScale | TransformMatrixParametersMatrixArray | TransformMatrixParametersProduct>
    >([
        {
            name: "Translation, Rotation, Scale",
            value: {
                type: "TranslationRotationScale",
                translation: [0, 0, 0],
                rotation: [0, 0, 0],
                scale: [1, 1, 1],
            },
        },
        {
            name: "Matrix Array",
            value: {
                type: "MatrixArray",
                array: [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1],
            },
        },
        {
            name: "Product",
            value: {
                type: "Product",
                termA: null,
                termB: null,
            },
        },
    ])

    transformTypeSelected = (
        selectionPossibility: SelectionPossibility<
            TransformMatrixParametersTranslationRotationScale | TransformMatrixParametersMatrixArray | TransformMatrixParametersProduct
        >,
    ) => {
        if (selectionPossibility.name === "Translation, Rotation, Scale") {
            return isTransformMatrixWithParametersTranslationRotationScale(this.$node())
        } else if (selectionPossibility.name === "Matrix Array") {
            return isTransformMatrixWithParametersMatrixArray(this.$node())
        } else if (selectionPossibility.name === "Product") {
            return isTransformMatrixWithParametersProduct(this.$node())
        }
        return false
    }

    readonly $transformMatrixWithParametersTranslationRotationScale = computed(() => {
        this.$parameters()
        const node = this.$node()
        if (isTransformMatrixWithParametersTranslationRotationScale(node)) return node
        return undefined
    })
    readonly $transformMatrixWithParametersMatrixArray = computed(() => {
        this.$parameters()
        const node = this.$node()
        if (isTransformMatrixWithParametersMatrixArray(node)) return node
        return undefined
    })
    readonly $transformMatrixWithParametersProduct = computed(() => {
        this.$parameters()
        const node = this.$node()
        if (isTransformMatrixWithParametersProduct(node)) return node
        return undefined
    })

    transformMatrixWithParametersMatrixArrayLabels = (() => {
        const labels = []
        for (let i = 0; i < 16; i++) labels.push(`${i}`)
        return labels
    })()
}
